/**
 * Enterprise nav route constants and href builders — the shared source for the org-wide Events
 * list path and per-event routes multiple features link to (Home, Details). Consolidated here
 * (out of `event-details/_logic/constants.ts`, an unrelated feature's constants file) so nav
 * promotion and redirect-consolidation logic don't duplicate route strings across features.
 */

/** Org-wide Events list — the sidebar's top-level landing page. */
export const ORG_EVENTS_PATH = "/dashboard/organization/events";

/** The signed-in person's own email settings, open to every client user, not only org admins. */
export const NOTIFICATION_SETTINGS_PATH = "/dashboard/settings/notifications";

/** Dashboard root — resolves the post-login landing destination, then redirects. */
export const DASHBOARD_PATH = "/dashboard";

/** Event-less Home, shown to a 0-event user. Not to be confused with {@link EVENT_HOME_PATH}. */
export const DASHBOARD_HOME_PATH = "/dashboard/home";

/** The create-event flow a client starts from the dashboard. */
export const CREATE_EVENT_PATH = "/create";

/** Event Home route template, `[event_id]` is the Next.js dynamic segment — for `revalidatePath`. */
export const EVENT_HOME_PATH = "/dashboard/home/[event_id]";

/**
 * Resolved href for a specific event's Home.
 *
 * @param eventId - The event to link to
 * @returns The event's Home path
 */
export function eventHomeHref(eventId: number): string {
  return EVENT_HOME_PATH.replace("[event_id]", String(eventId));
}

/** Event Details route template, `[event_id]` is the Next.js dynamic segment — for `revalidatePath`. */
export const EVENT_DETAILS_PATH = "/dashboard/details/[event_id]";

/**
 * Resolved href for a specific event's Details page.
 *
 * @param eventId - The event to link to
 * @returns The event's Details path
 */
export function eventDetailsHref(eventId: number): string {
  return EVENT_DETAILS_PATH.replace("[event_id]", String(eventId));
}

/** Event Notes route template, `[event_id]` is the Next.js dynamic segment — for `revalidatePath`. */
export const EVENT_NOTES_PATH = "/dashboard/notes/[event_id]";

/**
 * Resolved href for a specific event's Notes page.
 *
 * @param eventId - The event to link to
 * @returns The event's Notes path
 */
export function eventNotesHref(eventId: number): string {
  return EVENT_NOTES_PATH.replace("[event_id]", String(eventId));
}

/** Event Messages route template, `[event_id]` is the Next.js dynamic segment — for `revalidatePath`. */
export const EVENT_MESSAGES_PATH = "/dashboard/source/[event_id]/messages";

/**
 * Resolved href for a specific event's Messages page.
 *
 * @param eventId - The event to link to
 * @returns The event's Messages path
 */
export function eventMessagesHref(eventId: number): string {
  return EVENT_MESSAGES_PATH.replace("[event_id]", String(eventId));
}

/**
 * Resolved href for one conversation on an event, addressed by its RFP.
 *
 * @param eventId - The event to link to
 * @param rfpId - The `event_venue_candidate` the conversation is about
 * @returns The conversation's path
 */
export function eventConversationHref(eventId: number, rfpId: string): string {
  // Encoded because the contract types an id as any non-empty string. A backend
  // that ever sent one carrying a slash would address a different route from
  // here rather than fail on the way out.
  return `${eventMessagesHref(eventId)}/${encodeURIComponent(rfpId)}`;
}

/** Query param that seeds a conversation's composer with a draft the client can edit before sending. */
export const CONVERSATION_DRAFT_PARAM = "draft";

/**
 * Resolved href for one conversation, opened with a draft already in the composer.
 *
 * @param eventId - The event to link to
 * @param rfpId - The `event_venue_candidate` the conversation is about
 * @param draft - Words to put in the composer; nothing is sent until the client presses send
 * @returns The conversation href with the draft in its query
 * @throws Never.
 */
export function eventConversationDraftHref(eventId: number, rfpId: string, draft: string): string {
  const query = new URLSearchParams({ [CONVERSATION_DRAFT_PARAM]: draft });
  return `${eventConversationHref(eventId, rfpId)}?${query.toString()}`;
}

/** The restaurant-quotes tab's name (lunches too, so not "Dinners"), in the sidebar, the breadcrumb and the page title. */
export const DINING_LABEL = "Dining";

/** Event Dinners route template, `[event_id]` is the Next.js dynamic segment — for `revalidatePath`. */
export const EVENT_DINNERS_PATH = "/dashboard/source/[event_id]/dinners";

/**
 * Resolved href for a specific event's Dinners page (restaurant inquiries and quotes).
 *
 * @param eventId - The event to link to
 * @returns The event's Dinners path
 */
export function eventDinnersHref(eventId: number): string {
  return EVENT_DINNERS_PATH.replace("[event_id]", String(eventId));
}

// ---------------------------------------------------------------------------
// Organization settings tabs — for `revalidatePath` after a settings mutation
// ---------------------------------------------------------------------------

/** Organization settings Profile tab. */
export const ORG_SETTINGS_PROFILE_PATH = "/dashboard/organization/settings/profile";

/** Organization settings Office locations tab. */
export const ORG_SETTINGS_OFFICES_PATH = "/dashboard/organization/settings/office-location";

/** Organization settings Approval policies tab. */
export const ORG_SETTINGS_POLICIES_PATH = "/dashboard/organization/settings/policies";

/** Organization settings Preferred venues tab (venues, chains, vendors and their attachments). */
export const ORG_SETTINGS_PREFERRED_VENUES_PATH = "/dashboard/organization/settings/preferred-venues";

/** Organization settings Blacklisted venues tab (venues and chains). */
export const ORG_SETTINGS_BLACKLISTED_VENUES_PATH = "/dashboard/organization/settings/blacklisted-venues";

/** Organization settings Contracts tab (clauses and templates). */
export const ORG_SETTINGS_CONTRACTS_PATH = "/dashboard/organization/settings/contracts";

/** Organization settings Required questions tab (questions added to every RFP). */
export const ORG_SETTINGS_REQUIRED_QUESTIONS_PATH = "/dashboard/organization/settings/required-questions";

/** Organization > Settings > Integrations route. */
export const ORG_SETTINGS_INTEGRATIONS_PATH = "/dashboard/organization/settings/integrations";
